import { Layer, Rect, Stage } from "react-konva";
import { AutoSizer } from "../AutoSizer";
import { useMemo } from "react";
import PrimitiveConfig from "../PrimitiveConfig";
import { mixHex } from "../RenderHelpers";

export function SlideStructurePreview({
  primitive,
  scale: maxScale = 1,
  width = 1920,
  height = 1080,
  enableResizeObserver = true,
  className = "",
  style,
  slide,
  stroke,
  shadowEnabled=true,
  gradientValues,
  ...stageProps
}) {
  const padding = [10, 20];
  const debugEnabled =
    typeof window !== "undefined" &&
    (window.__WF_THUMB_DEBUG === true || window.localStorage?.getItem("wfThumbDebug") === "1");

  const items = useMemo(()=>{
    const out = [
              <Rect
                fill="white"
                stroke="#333"
                width={width}
                height={height}
                shadowBlur={20}
                shadowColor="#aaa"
                shadowEnabled={shadowEnabled}
                shadowOffsetX={15}
                shadowOffsetY={15}
              />
    ]
    if( !slide ){
        return out
    }

    const background = "#ffffff"
    const [gradientStart, gradientMid, gradientEnd] = gradientValues ?? [
      "#e2e8f0",
      "#cbd5f5",
      "#bfdbfe",
    ];
    const lighterStart = mixHex(gradientStart, background, 0.2)
    const lighterMid = mixHex(gradientMid, background, 0.3)
    const lighterEnd = mixHex(mixHex(gradientMid, gradientEnd), background, 0.4)
    const fillLinearGradientColorStops = [0, lighterStart, 0.6, lighterMid, 1, lighterEnd]
    const element =
      slide?.primitives?.origin?.allElement
      ?? slide?.primitives?.origin?.allUniqueElement
      ?? []
    const frameKeys = Object.keys(slide?.frames ?? {})
    let skippedMissingFrame = 0
    let skippedTooSmallShape = 0
    let skippedInvalidSize = 0
    let renderedCount = 0

    if (debugEnabled) {
      console.debug("[wf-thumb] start", {
        slideId: slide?.id,
        slideTitle: slide?.title,
        elementCount: element.length,
        frameCount: frameKeys.length,
        sampleElementIds: element.slice(0, 8).map((d) => d?.id),
        sampleFrameKeys: frameKeys.slice(0, 8),
      })
    }

    element.forEach(d=>{
        const layout = slide.frames[d.id] ?? {}
        if (!slide?.frames?.[d?.id]) {
          skippedMissingFrame += 1
        }
        const x = layout.x ?? 0
        const y = layout.y ?? 0
        const scale = Number.isFinite(Number(layout.s)) && Number(layout.s) > 0 ? Number(layout.s) : 1
        const rawWidth = Number(layout.placeholder?.sizing?.width ?? layout.width ?? 100)
        const rawHeight = Number(layout.placeholder?.sizing?.height ?? layout.height ?? 100)
        const width = rawWidth * scale
        const height = rawHeight * scale
        const isShape = d.referenceId === PrimitiveConfig.Constants.SHAPE_ELEMENT
        const isBackground = isShape && d.referenceParameters?.style !== "text"
        const cornerRadius = isShape ? 0 : 25
        if( isShape && (height < 30 || width < 30)){
            skippedTooSmallShape += 1
            return
        }        
        if( width === 0 || height === 0 || !isFinite(width) || !isFinite(height)){
            skippedInvalidSize += 1
            return
        }
        renderedCount += 1

        out.push( <Rect 
                    x={x} 
                    y={y} 
                    width={width} 
                    height={height} 
                    cornerRadius={cornerRadius}
                    strokeWidth={isShape ? 0 : 1}
                    stroke={stroke}
                    fillLinearGradientStartPoint={{ x: width * 0.5, y: 0 }}
                    fillLinearGradientEndPoint={{ x: width * 0.5, y: height  * 1.5}}
                    fillLinearGradientColorStops={fillLinearGradientColorStops}
                    /> )
    })

    if (debugEnabled) {
      console.debug("[wf-thumb] result", {
        slideId: slide?.id,
        renderedCount,
        skippedMissingFrame,
        skippedTooSmallShape,
        skippedInvalidSize,
      })
    }

    return out
  }, [slide, width, height, gradientValues, stroke, debugEnabled]);

  return (
    <AutoSizer
      enableResizeObserver={enableResizeObserver}
      className={className}
      style={style}
    >
      {({ width: sWidth, height: sHeight }) => {
        const fullWidth = width + padding[1] * 2;
        const fullHeight = height + padding[0] * 2;
        const computedScale = Math.min(
          maxScale,
          sWidth / fullWidth,
          sHeight / fullHeight
        );
        const ox = ((sWidth - fullWidth * computedScale) / 2) / computedScale;
        const oy = ((sHeight - fullHeight * computedScale) / 2) / computedScale;

        return (
          <Stage
            width={sWidth}
            height={sHeight}
            scaleX={computedScale}
            scaleY={computedScale}
            offsetX={-ox}
            offsetY={-oy}
            {...stageProps}
          >
            <Layer perfectDrawEnabled={false} listening={false}>
                {items}
            </Layer>
          </Stage>
        );
      }}
    </AutoSizer>
  );
}
